import { useRef } from 'react';
import { useEscapeToClose, useFocusTrap } from '../../motion';
import type { Issue } from '../../types';
import type { IssueType } from '../../issue-types';
import type { Profiles } from '../../people';
import { ACTUAL_PARKED_LABEL, STATUS_LABELS } from '../../types';
import { labelOf, meansOf, meansMark, NEW_BOARD_TYPES, ORIGINAL_TYPES } from '../../issue-types';
import { blockerOf } from '../../blocked';
import { blockerSummary } from '../../unblock';
import { ageInDays } from '../../card-age';
import { initials } from '../../people';
import VendorTag from '../VendorTag';
import Priority from '../Priority';
import Icon from '../Icon';

/**
 * THE LEAN CARD DETAIL — a portfolio card, opened READ-ONLY in place.
 *
 * Answers "Why can't I drill into these cards — all I can see is the title, no
 * due date?" (Vinnie, 19 Sep). Click a card on either portfolio lens (a project
 * row's task detail, or the by-person list) and this opens OVER the portfolio
 * without leaving it — the facts a weekly meeting reads: what it's blocked on +
 * why + how long, status · priority · owner, type, start & due date (overdue
 * flagged), the latest update, and what the card is about.
 *
 * It opens the SAME WAY as a card everywhere else in the app (Vinnie, 6 Oct:
 * "the portfolio read-only cards should be opened the same way … so they slide
 * in from the right"): the app's own slide-over (InvoiceDrawer's .inv-drawer /
 * .drw-* frame, the one My Work and the budget ledger use), docked right, not a
 * centred modal. It is built from the app's OWN read-view parts (read-facts,
 * read-grid, read-summary, the blocked banner, the status pill, Priority, the
 * assignee) so it IS the same card — trimmed to those facts and stripped of
 * every edit control. "Open the full card" is the one way through to editing,
 * and it leaves the portfolio for the board.
 *
 * Read-only and cross-board on purpose: it runs on the cards the roll-up already
 * loaded (store.portfolioIssues), so it costs no read. The one thing it cannot
 * read cross-board is a board's RENAMED type names — so the Type is resolved
 * against the active board's types first, then the standard seed vocabularies as
 * a fallback (correct for every standard type; a board's custom rename of a type
 * on ANOTHER board falls back to the slug, which beats saying nothing).
 */

import { MONTHS } from '../../when';
const DAY_MS = 86_400_000;

/** A `YYYY-MM-DD` → "5 Sep 2026". */
function fmtFull(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return `${d} ${MONTHS[m - 1]} ${y}`;
}

/** A ms stamp → "31 Aug", for the "since" on a blocker. */
function fmtMs(ms: number): string {
  const dt = new Date(ms);
  return `${dt.getDate()} ${MONTHS[dt.getMonth()]}`;
}

/** Days a `YYYY-MM-DD` is past today, 0 if not past or unset. "Today" is the
 *  LOCAL calendar day (same footing as my-work's dueDaysOf and slipWord below);
 *  flooring `now` to a UTC-midnight boundary read a day ahead every evening in a
 *  behind-UTC timezone, so the same card showed "4 days" here and "3 days" on the
 *  full card and My Work. */
function overdueOf(due: string | undefined, closed: boolean, now: number): number {
  if (!due || closed) return 0;
  const [y, m, d] = due.split('-').map(Number);
  if (!y || !m || !d) return 0;
  const dueMs = Date.UTC(y, m - 1, d);
  const t = new Date(now);
  const today = Date.UTC(t.getFullYear(), t.getMonth(), t.getDate());
  return dueMs < today ? Math.round((today - dueMs) / DAY_MS) : 0;
}

/** A ms instant → "18 Sep 2026" — the full form fmtFull gives a planned day, so
 *  planned and actual read the same way. */
function fmtMsFull(ms: number): string {
  const dt = new Date(ms);
  return `${dt.getDate()} ${MONTHS[dt.getMonth()]} ${dt.getFullYear()}`;
}
/** The ms instant as a plain local `YYYY-MM-DD`, so it diffs against a planned
 *  day on the same calendar footing. */
function msIsoDay(ms: number): string {
  const dt = new Date(ms);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}
/** "2 days late" / "3 days early" / "on the day", or null with no planned day. */
function slipWord(actualIso: string, plannedIso?: string): string | null {
  if (!plannedIso) return null;
  const u = (iso: string) => { const [y, m, d] = iso.split('-').map(Number); return Date.UTC(y, m - 1, d); };
  const n = Math.round((u(actualIso) - u(plannedIso)) / DAY_MS);
  if (n === 0) return 'on the day';
  const days = Math.abs(n);
  return `${days} day${days === 1 ? '' : 's'} ${n > 0 ? 'late' : 'early'}`;
}

export default function LeanCardDetail({
  issue,
  context,
  issueTypes,
  profiles,
  resolveName,
  now = Date.now(),
  leaving = false,
  onClose,
  onOpenFull,
}: {
  issue: Issue;
  /** The board/project the card sits on, e.g. "Finance system upgrade · Board". */
  context: string;
  /** The ACTIVE board's types — the first place the card's type is resolved. */
  issueTypes: IssueType[];
  profiles: Profiles;
  resolveName: (who: string) => string;
  now?: number;
  /** True while the drawer is sliding out — focus trap + Escape stand down. */
  leaving?: boolean;
  onClose: () => void;
  /** Leave the portfolio for the real card on its board (switch + open). */
  onOpenFull: () => void;
}) {
  const panelRef = useRef<HTMLElement>(null);
  useFocusTrap(panelRef, !leaving);
  // Escape closes it, like every other overlay on the app (the slide-over runs
  // the exit; see the scene in Portfolio.tsx).
  useEscapeToClose(onClose, !leaving);

  const closed = issue.status === 'done' || issue.status === 'wontdo';

  // Type — active board's types first, then the standard seed vocabularies, so a
  // standard slug always resolves to its real name/mark and the meaning is right.
  const known: IssueType[] = [
    ...issueTypes,
    ...NEW_BOARD_TYPES.map((t, i) => ({ ...t, id: `nb-${i}` })),
    ...ORIGINAL_TYPES,
  ];
  const typeLabel = labelOf(issue.kind, known);
  const means = meansOf(issue.kind, known);
  const mark = meansMark(means);

  const start = issue.startsOn || '';
  const due = issue.dueDate || '';
  const overdueDays = overdueOf(due || undefined, closed, now);

  // Blocked — read exactly as the opened card reads it (a word that means nothing
  // is not a blocker; blockerOf gates that). The lead names who/what, then the
  // "since" date and the age, so the meeting sees how long it has waited.
  const why = blockerOf(issue);
  const lead = blockerSummary(issue);
  const age = why ? ageInDays(issue.blockedAt, now) : null;
  const since = why && issue.blockedAt ? fmtMs(issue.blockedAt) : null;
  const blockedAction = [lead, since ? `since ${since}` : null, age != null ? `${age}d` : null]
    .filter(Boolean)
    .join(' · ');

  const stands = (issue.stands ?? '').trim();
  const about = (issue.summary ?? '').trim() || (issue.description ?? '').trim();

  return (
    <aside
      ref={panelRef}
      tabIndex={-1}
      className={`inv-drawer lcd-drawer lcd ${leaving ? 'is-leaving' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-label={`${issue.ref} — ${issue.title}`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="drw-head">
        <div className="lcd-headl">
          <span className="lcd-context">
            {issue.ref}
            {issue.ref && ' · '}
            {context}
          </span>
          <span className="id-title">{issue.title}</span>
        </div>
        <div className="id-head-right">
          <span className="lcd-ro"><Icon name="eye" size={14} />Read-only</span>
          <button className="drw-close" onClick={onClose} title="Close (Escape)" aria-label="Close (Escape)">
            <Icon name="close" size={17} />
          </button>
        </div>
      </div>

      <div className="drw-body lcd-body">
          {why && (
            <div className="blocked-banner">
              <div className="blocked-head">
                <span className="blocked-label">Blocked</span>
                {blockedAction && <span className="blocked-action">{blockedAction}</span>}
              </div>
              <span className="blocked-why">{why}</span>
            </div>
          )}

          <div className="read-facts">
            <div className="read-primary">
              <span className={`status-pill dot-bg-${issue.status}`}>{STATUS_LABELS[issue.status]}</span>
              <Priority priority={issue.priority} />
              {issue.assignee ? (
                <span className="read-who">
                  <span className="avatar" title={`Assigned to ${resolveName(issue.assignee)}`}>
                    {initials(issue.assignee, profiles)}
                  </span>
                  {resolveName(issue.assignee)}
                  <VendorTag who={issue.assignee} profiles={profiles} compact />
                </span>
              ) : (
                <span className="lcd-unassigned">Unassigned</span>
              )}
            </div>

            <div className="read-details">
              <div className="read-details-h">Details</div>
              <dl className="read-grid">
                <div className="read-row">
                  <dt>Type</dt>
                  <dd>
                    <span className={`read-type ${means}`}>
                      <Icon name={mark} size={16} />
                      {typeLabel}
                    </span>
                  </dd>
                </div>
                {(() => {
                  // PLANNED vs ACTUAL — the same read the opened card gives: the
                  // dates a person set, beside the start/finish the system stamped,
                  // so the meeting sees the slip in words. (Mirrors IssueModal.)
                  const startedAt = issue.startedAt;
                  const completedAt = issue.completedAt;
                  const hasActual = startedAt != null || completedAt != null;
                  if (!start && !due && !hasActual) return null;
                  const notes: string[] = [];
                  if (start && startedAt != null) { const w = slipWord(msIsoDay(startedAt), start); if (w) notes.push(`Started ${w}`); }
                  if (due && completedAt != null) { const w = slipWord(msIsoDay(completedAt), due); if (w) notes.push(`finished ${w}`); }
                  const finishedLate = !!(due && completedAt != null && slipWord(msIsoDay(completedAt), due)?.endsWith('late'));
                  return (
                    <>
                      {(start || due) && (
                        <div className="read-row">
                          <dt>{hasActual ? 'Planned' : 'Dates'}</dt>
                          <dd>
                            <span className="read-dates">
                              {start && due ? (
                                <>
                                  {fmtFull(start)}
                                  <span className="read-dates-arrow" aria-hidden="true">→</span>
                                  <span className={overdueDays > 0 ? 'read-over' : undefined}>{fmtFull(due)}</span>
                                </>
                              ) : start ? (
                                <>From {fmtFull(start)}</>
                              ) : (
                                <span className={overdueDays > 0 ? 'read-over' : undefined}>Due {fmtFull(due)}</span>
                              )}
                            </span>
                            {overdueDays > 0 && (
                              <span className="read-date-warn">
                                Overdue by {overdueDays} day{overdueDays === 1 ? '' : 's'}
                              </span>
                            )}
                          </dd>
                        </div>
                      )}
                      {hasActual && (
                        <div className="read-row">
                          <dt>Actual</dt>
                          <dd>
                            <span className="read-dates">
                              {startedAt != null && completedAt != null ? (
                                <>
                                  {fmtMsFull(startedAt)}
                                  <span className="read-dates-arrow" aria-hidden="true">→</span>
                                  <span className={finishedLate ? 'read-over' : undefined}>{fmtMsFull(completedAt)}</span>
                                </>
                              ) : startedAt != null ? (
                                // In step with the lane (see IssueModal): actively-worked cards
                                // show only the real start; a card set back to To Do / Backlog
                                // reads "· on hold" rather than claiming it is still running.
                                issue.status === 'inprogress' || issue.status === 'inreview' ? (
                                  <>Started {fmtMsFull(startedAt)}</>
                                ) : (
                                  <>Started {fmtMsFull(startedAt)} · <span className="read-parked">{ACTUAL_PARKED_LABEL}</span></>
                                )
                              ) : (
                                <>Finished {fmtMsFull(completedAt!)}</>
                              )}
                            </span>
                            {notes.length > 0 && <span className="read-actual-note">{notes.join(' · ')}</span>}
                          </dd>
                        </div>
                      )}
                    </>
                  );
                })()}
              </dl>
            </div>
          </div>

          {stands && (
            <div className="lcd-sec">
              <div className="lcd-h">Where it stands now</div>
              <div className="read-summary">{stands}</div>
            </div>
          )}

          {about && (
            <div className="lcd-sec">
              <div className="lcd-h">What this is about</div>
              <div className="read-summary">{about}</div>
            </div>
          )}
      </div>

      <div className="drw-foot">
        <span className="id-foot-note">Read-only snapshot · already loaded, no navigation</span>
        <button className="btn btn-small" onClick={onOpenFull}>
          Open the full card <Icon name="chevron" size={14} />
        </button>
      </div>
    </aside>
  );
}
